/**
 * Lot과 Pip 계산 유틸리티 함수들
 * 외환 거래에서 Lot 크기와 Pip 가치를 계산하는 함수들
 */
import { multiply, divide, product, add } from '../decimal';
/**
 * 1Lot 값 계산
 * @param currentPrice - 기준통화 시세의 매도가
 * @param metadata - 심볼 메타데이터
 * @returns 1Lot의 실제 가치 (기준통화 기준)
 */
export const calculateLotValue = (currentPrice, metadata) => {
    // 1. Lot 승수 적용
    const lotSize = metadata.lotMultiple;
    // 2. 현재가에 Price 승수 적용 (부동소수점 오차 방지)
    const adjustedPrice = multiply(currentPrice, metadata.priceMultiplier);
    // 3. 1Lot의 실제 가치 계산 (부동소수점 오차 방지)
    const lotValue = multiply(lotSize, adjustedPrice);
    return lotValue;
};
/**
 * Pip 값 계산 (1 Pip 변화 시 손익)
 * @param metadata - 심볼 메타데이터
 * @param currentPrice - 거래통화 시세의 매도가
 * @returns 1 Pip의 가치
 */
export const calculatePipValue = (metadata, currentPrice) => {
    // 1. 1 Pip의 가치 계산 (부동소수점 오차 방지)
    const pipValue = product(metadata.pipUnit, metadata.lotMultiple, metadata.priceMultiplier, currentPrice);
    return pipValue;
};
/**
 * 총 거래 가치 계산 (수량 × 1Lot 가치)
 * @param symbol - 종목코드
 * @param currentPrice - 현재가
 * @param lotSize - 거래 수량 (Lots)
 * @param metadata - 심볼 메타데이터
 * @returns 총 거래 가치
 */
export const calculateTotalValue = (currentPrice, lotSize, metadata) => {
    const lotValue = calculateLotValue(currentPrice, metadata);
    // 부동소수점 오차 방지를 위해 decimal.js 사용
    return multiply(lotValue, lotSize);
};
/**
 * 증거금 계산 (마진 요구사항)
 * @param symbol - 종목코드
 * @param currentPrice - 현재가
 * @param lotSize - 거래 수량 (Lots)
 * @param metadata - 심볼 메타데이터
 * @returns 필요한 증거금
 */
export const calculateMargin = (currentPrice, lotSize, metadata) => {
    if (!metadata.isMarginUsage) {
        return 0;
    }
    const totalValue = calculateTotalValue(currentPrice, lotSize, metadata);
    // 부동소수점 오차 방지를 위해 decimal.js 사용
    const marginRateDecimal = divide(metadata.marginRate, 100);
    return multiply(totalValue, marginRateDecimal);
};
/** 증거금유형코드: 수량기준 */
export const MARGIN_TYPE_CODE_QUANTITY = 'QUANTITY';
/** 증거금유형코드: 금액기준 */
export const MARGIN_TYPE_CODE_AMOUNT = 'AMOUNT';
/**
 * 수량기준 증거금유형 여부 (mgn_tycd: 1)
 * @param marginTypeCode - 증거금유형코드
 * @returns 수량기준이면 true
 */
export const isQuantityBasedMarginType = (marginTypeCode) => {
    return String(marginTypeCode).toUpperCase() === MARGIN_TYPE_CODE_QUANTITY;
};
/**
 * 금액기준 증거금유형 여부 (mgn_tycd: 2)
 * @param marginTypeCode - 증거금유형코드
 * @returns 금액기준이면 true
 */
export const isAmountBasedMarginType = (marginTypeCode) => {
    return String(marginTypeCode).toUpperCase() === MARGIN_TYPE_CODE_AMOUNT;
};
/**
 * 최소 증거금 계산 (수량기준)
 * @param marginRate - 증거금율(%)
 * @param lotValue - 1Lot 값
 * @returns 최소 증거금
 */
export const calculateMinimumMargin = (marginRate, lotValue) => {
    const marginRateDecimal = divide(marginRate, 100);
    return multiply(marginRateDecimal, lotValue);
};
/**
 * 최소 증거금 계산 (금액기준)
 * Lot승수 × 중간가 / 증거금적용환율 × (증거금률 / 100)
 * @param marginRate - 증거금율(%)
 * @param lotMultiple - Lot승수
 * @param buyPrice - 매수호가
 * @param sellPrice - 매도호가
 * @param currencyRateToUsd - 1 / 증거금적용환율 (TRDUSD 또는 1/USDTRD, 거래통화 USD면 1)
 * @returns 최소 증거금
 */
export const calculateNonFxMinimumMargin = (marginRate, lotMultiple, buyPrice, sellPrice, currencyRateToUsd) => {
    const midPrice = divide(add(buyPrice, sellPrice), 2);
    const marginRateDecimal = divide(marginRate, 100);
    return product(lotMultiple, midPrice, currencyRateToUsd, marginRateDecimal);
};
/**
 * 증거금유형코드에 따른 최소 증거금 계산
 * - 수량기준(QUANTITY): 1 Lot 값 × 증거금률 / 100
 * - 금액기준(AMOUNT): Lot승수 × 중간가 / 증거금적용환율 × 증거금률 / 100
 * @param params - 계산 파라미터
 * @param params.marginTypeCode - 증거금유형코드 (QUANTITY | AMOUNT)
 * @param params.currencyRateToUsd - 1 / 증거금적용환율
 * @returns 최소 증거금
 */
export const calculateMinimumMarginByMarginTypeCode = (params) => {
    const { marginTypeCode, marginRate, lotValue, lotMultiple, buyPrice, sellPrice, currencyRateToUsd, } = params;
    if (isQuantityBasedMarginType(marginTypeCode)) {
        return calculateMinimumMargin(marginRate, lotValue);
    }
    return calculateNonFxMinimumMargin(marginRate, lotMultiple, buyPrice, sellPrice, currencyRateToUsd);
};
/** 수량기준 1 Lot 증거금 계산식 라벨 */
export const QUANTITY_MARGIN_FORMULA_LABEL = '수량기준: 1 Lot 값 × 증거금률 / 100';
/** 금액기준 1 Lot 증거금 계산식 라벨 */
export const AMOUNT_MARGIN_FORMULA_LABEL = '금액기준: Lot승수 × 중간가 / 증거금적용환율 × 증거금률 / 100';
/**
 * 주문 화면 1 Lot 증거금 계산 (검증 + 계산)
 * @param input - 계산 입력
 * @returns 계산 결과
 */
export const resolveOrderMinimumMargin = (input) => {
    const { stockCd, marginTypeCode, marginRate, lotValue, lotMultiple, buyPrice, sellPrice, currencyRateToUsd, hasMetadata, hasSymbolData, } = input;
    const baseInputs = {
        marginRate: marginRate ?? NaN,
        lotValue,
        lotMultiple,
        buyPrice,
        sellPrice,
        currencyRateToUsd,
    };
    const buildResolution = (partial) => {
        const resolution = {
            stockCd,
            marginTypeCode,
            ...partial,
            inputs: { ...baseInputs, ...partial.inputs },
        };
        return resolution;
    };
    if (marginRate === null || marginRate === undefined) {
        return buildResolution({
            value: null,
            skipReason: 'MISSING_MARGIN_RATE',
            formulaLabel: '증거금율 미로드',
        });
    }
    if (!hasMetadata) {
        return buildResolution({
            value: null,
            skipReason: 'MISSING_METADATA',
            formulaLabel: '메타데이터 없음',
        });
    }
    if (!hasSymbolData) {
        return buildResolution({
            value: null,
            skipReason: 'MISSING_SYMBOL_DATA',
            formulaLabel: '종목 시세 없음',
        });
    }
    if (isQuantityBasedMarginType(marginTypeCode)) {
        if (!lotValue) {
            return buildResolution({
                value: null,
                skipReason: 'MISSING_LOT_VALUE',
                formulaLabel: QUANTITY_MARGIN_FORMULA_LABEL,
            });
        }
        const value = calculateMinimumMargin(marginRate, lotValue);
        return buildResolution({
            value,
            formulaLabel: QUANTITY_MARGIN_FORMULA_LABEL,
            inputs: { appliedMarginRate: marginRate / 100 },
        });
    }
    if (isAmountBasedMarginType(marginTypeCode)) {
        if (!buyPrice || !sellPrice) {
            return buildResolution({
                value: null,
                skipReason: 'MISSING_QUOTES',
                formulaLabel: AMOUNT_MARGIN_FORMULA_LABEL,
            });
        }
        if (!currencyRateToUsd) {
            return buildResolution({
                value: null,
                skipReason: 'MISSING_TRADE_CURRENCY_RATE',
                formulaLabel: AMOUNT_MARGIN_FORMULA_LABEL,
            });
        }
        const midPrice = divide(add(buyPrice, sellPrice), 2);
        const value = calculateNonFxMinimumMargin(marginRate, lotMultiple, buyPrice, sellPrice, currencyRateToUsd);
        return buildResolution({
            value,
            formulaLabel: AMOUNT_MARGIN_FORMULA_LABEL,
            inputs: {
                midPrice,
                appliedMarginRate: marginRate / 100,
            },
        });
    }
    return buildResolution({
        value: null,
        skipReason: 'UNKNOWN_MARGIN_TYPE_CODE',
        formulaLabel: '알 수 없는 증거금유형코드',
    });
};
/**
 * 포지션 평가손익 계산
 * @param currentPrice - 현재가
 * @param bookPrice - 장부가격
 * @param quantity - 보유 수량
 * @param positionCd - 포지션 코드 (LONG/SHORT)
 * @param metadata - 심볼 메타데이터 (lotMultiple, priceMultiplier 등)
 * @param currencyRate - 거래 통화 환율 (기본값 1)
 * @returns 평가손익 값
 */
export const calculatePositionProfitLoss = ({ currentPrice, bookPrice, quantity, positionCd, metadata, currencyRate = 1, }) => {
    if (!Number.isFinite(currentPrice) || !Number.isFinite(bookPrice)) {
        return 0;
    }
    const priceDiff = currentPrice - bookPrice;
    const isLong = positionCd === 'LONG';
    const baseProfitLoss = isLong ? priceDiff : -priceDiff;
    const lotMultiple = metadata?.lotMultiple ?? 1;
    const priceMultiplier = metadata?.priceMultiplier ?? 1;
    // 부동소수점 오차 방지를 위해 decimal.js 사용
    return product(baseProfitLoss, quantity, lotMultiple, priceMultiplier, currencyRate);
};
/**
 * 스프레드 비용 계산
 * @param symbol - 종목코드
 * @param lotSize - 거래 수량 (Lots)
 * @param metadata - 심볼 메타데이터
 * @returns 스프레드 비용
 */
export const calculateSpreadCost = (symbol, lotSize, metadata) => {
    if (!metadata.isSpreadUsage) {
        return 0;
    }
    const baseLotSize = 100000;
    // 부동소수점 오차 방지를 위해 decimal.js 사용
    const actualLotSize = product(baseLotSize, metadata.lotMultiple, lotSize);
    // 매수/매도 스프레드의 평균값 사용 (부동소수점 오차 방지)
    const averageSpread = divide(add(metadata.buySpread, metadata.sellSpread), 2);
    return multiply(actualLotSize, averageSpread);
};
/**
 * 포맷된 가격 문자열 생성 (Lot/Pip 전용)
 * @param value - 가격 값
 * @param decimals - 소수점 자릿수 (기본값: 2)
 * @returns 포맷된 가격 문자열
 */
export const formatLotPrice = (value, decimals = 2) => {
    return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};
/**
 * 포맷된 통화 문자열 생성 (Lot/Pip 전용)
 * @param value - 금액
 * @param currency - 통화 코드 (기본값: 'USD')
 * @param decimals - 소수점 자릿수 (기본값: 2)
 * @returns 포맷된 통화 문자열
 */
export const formatLotCurrency = (value, currency = 'USD', decimals = 2) => {
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};
//# sourceMappingURL=lot-pip-calculator.js.map